/**
 * Document Folder — Shared workspace for Admin, Controller, Bkkpr Mgr, Irit (T4)
 *
 * Features:
 * - Free-form folder/subfolder creation
 * - Drag-and-drop file upload (PDF, Word, Excel, images) — max 25 MB
 * - Breadcrumb navigation
 * - In-browser preview panel for PDFs and images (side panel)
 * - Download and delete files/folders
 * - Role-gated: only allowed roles can access
 */

import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

// ── Allowed roles ──────────────────────────────────────────────────────────────
const ALLOWED_ROLES = ["super_admin", "admin", "controller", "bookkeeping_manager", "bookkeeper", "bookkeeper_supervisor", "tax_division"];
// Shobhit has read-only access to the shared Documents space (mirrors Google Drive restriction)
const DOCS_READONLY_EMAIL = "shobhit@advancefinserv.com";

// ── File type helpers ──────────────────────────────────────────────────────────
const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

function getFileIcon(mimeType: string): string {
  if (mimeType === "application/pdf") return "📄";
  if (mimeType.includes("word")) return "📝";
  if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) return "📊";
  if (mimeType.startsWith("image/")) return "🖼️";
  return "📎";
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string | Date): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function isPreviewable(mimeType: string): boolean {
  return mimeType === "application/pdf" || mimeType.startsWith("image/");
}

// ── Breadcrumb type ────────────────────────────────────────────────────────────
interface BreadcrumbItem {
  id: number | null;
  name: string;
}

interface PreviewFile {
  id: number;
  name: string;
  mimeType: string;
  size: number;
  s3Url: string;
  uploadedByName: string;
  createdAt: string | Date;
}

// ── Preview Panel ──────────────────────────────────────────────────────────────
function PreviewPanel({
  file,
  onClose,
  onDownload,
}: {
  file: PreviewFile;
  onClose: () => void;
  onDownload: (url: string, name: string) => void;
}) {
  const isPdf = file.mimeType === "application/pdf";
  const isImage = file.mimeType.startsWith("image/");

  return (
    <div style={{
      position: "fixed",
      top: 0, right: 0, bottom: 0,
      width: "min(520px, 90vw)",
      background: "oklch(0.94 0.018 220)",
      borderLeft: "1px solid oklch(0.90 0.009 240)",
      display: "flex",
      flexDirection: "column",
      zIndex: 50,
      boxShadow: "-8px 0 32px oklch(0 0 0 / 40%)",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px",
        borderBottom: "1px solid oklch(0.97 0.005 85)",
        background: "oklch(0.95 0.006 240)",
        flexShrink: 0,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: "14px", fontWeight: 600, color: "oklch(0.18 0.02 240)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {getFileIcon(file.mimeType)} {file.name}
          </div>
          <div style={{ fontSize: "13px", color: "oklch(0.14 0.04 240)", marginTop: "2px" }}>
            {formatSize(file.size)} · Uploaded by {file.uploadedByName} · {formatDate(file.createdAt)}
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", marginLeft: "12px", flexShrink: 0 }}>
          <button
            onClick={() => onDownload(file.s3Url, file.name)}
            title="Download"
            style={{
              padding: "6px 14px", borderRadius: "6px", fontSize: "13px", fontWeight: 600,
              background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)",
              border: "1px solid oklch(0.92 0.005 240 / 35%)", cursor: "pointer",
            }}
          >
            ↓ Download
          </button>
          <button
            onClick={onClose}
            title="Close preview"
            style={{
              padding: "6px 10px", borderRadius: "6px", fontSize: "14px",
              background: "oklch(0.94 0.018 220)", color: "oklch(0.14 0.04 240)",
              border: "1px solid oklch(0.88 0.01 240)", cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Preview content */}
      <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {isPdf && (
          <iframe
            src={`${file.s3Url}#toolbar=1&navpanes=0`}
            style={{ width: "100%", height: "100%", border: "none" }}
            title={file.name}
          />
        )}
        {isImage && (
          <div style={{
            width: "100%", height: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "16px",
            overflow: "auto",
            background: "oklch(0.94 0.008 240)",
          }}>
            <img
              src={file.s3Url}
              alt={file.name}
              style={{
                maxWidth: "100%", maxHeight: "100%",
                objectFit: "contain",
                borderRadius: "6px",
                boxShadow: "0 4px 24px oklch(0 0 0 / 50%)",
              }}
            />
          </div>
        )}
        {!isPdf && !isImage && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", height: "100%",
            color: "oklch(0.14 0.04 240)", fontSize: "14px", gap: "12px",
          }}>
            <div style={{ fontSize: "48px" }}>{getFileIcon(file.mimeType)}</div>
            <p style={{ margin: 0, textAlign: "center" }}>
              Preview not available for this file type.<br />
              <span style={{ color: "oklch(0.08 0.01 240)", cursor: "pointer" }}
                onClick={() => onDownload(file.s3Url, file.name)}>
                Download to view
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function DocumentFolder() {
  const { user } = useAuth();
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: null, name: "Documents" }]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const userEmail = user?.email ?? "";
  const userName = user?.name ?? "Unknown";

  // Role gate
  const hasAccess = user && ALLOWED_ROLES.includes((user as any).role ?? "");
  // Write gate — Shobhit is read-only
  const canWrite = hasAccess && userEmail.toLowerCase() !== DOCS_READONLY_EMAIL;

  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.docs.listFolder.useQuery(
    { callerEmail: userEmail, folderId: currentFolderId },
    { enabled: !!userEmail && !!hasAccess }
  );

  const folders = data?.folders ?? [];
  const files = data?.files ?? [];

  const createFolderMutation = trpc.docs.createFolder.useMutation({
    onSuccess: () => {
      utils.docs.listFolder.invalidate();
      setShowNewFolder(false);
      setNewFolderName("");
      toast.success("Folder created");
    },
    onError: (err) => toast.error("Failed to create folder: " + err.message),
  });

  const deleteFolderMutation = trpc.docs.deleteFolder.useMutation({
    onSuccess: () => {
      utils.docs.listFolder.invalidate();
      toast.success("Folder deleted");
    },
    onError: (err) => toast.error("Failed to delete folder: " + err.message),
  });

  const deleteFileMutation = trpc.docs.deleteFile.useMutation({
    onSuccess: () => {
      utils.docs.listFolder.invalidate();
      if (previewFile) setPreviewFile(null);
      toast.success("File deleted");
    },
    onError: (err) => toast.error("Failed to delete file: " + err.message),
  });

  const uploadFileMutation = trpc.docs.uploadFile.useMutation({
    onSuccess: () => {
      utils.docs.listFolder.invalidate();
      toast.success("File uploaded");
    },
    onError: (err) => toast.error("Upload failed: " + err.message),
  });

  // Navigate into a folder
  const openFolder = (folder: { id: number; name: string }) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setPreviewFile(null);
  };

  // Navigate via breadcrumb
  const navigateTo = (item: BreadcrumbItem, idx: number) => {
    setCurrentFolderId(item.id);
    setBreadcrumbs((prev) => prev.slice(0, idx + 1));
    setPreviewFile(null);
  };

  // Upload handler
  const handleFiles = useCallback(async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const validFiles: File[] = [];
    for (const file of Array.from(fileList)) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" is not a supported file type`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" exceeds the 25 MB limit`);
        continue;
      }
      validFiles.push(file);
    }
    if (validFiles.length === 0) return;

    setUploading(true);
    try {
      for (const file of validFiles) {
        const buffer = await file.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), "")
        );
        await uploadFileMutation.mutateAsync({
          folderId: currentFolderId,
          name: file.name,
          mimeType: file.type,
          size: file.size,
          fileDataBase64: base64,
          callerEmail: userEmail,
          callerName: userName,
        });
      }
    } finally {
      setUploading(false);
    }
  }, [currentFolderId, userEmail, userName, uploadFileMutation]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    createFolderMutation.mutate({
      name: newFolderName.trim(),
      parentId: currentFolderId,
      callerEmail: userEmail,
      callerName: userName,
    });
  };

  const handleDownload = (url: string, name: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleFileClick = (file: PreviewFile) => {
    if (previewFile?.id === file.id) {
      setPreviewFile(null);
    } else {
      setPreviewFile(file);
    }
  };

  // ── Access denied ──────────────────────────────────────────────────────────
  if (!hasAccess) {
    return (
      <DashboardLayout>
        <div style={{
          textAlign: "center", padding: "80px 24px",
          color: "oklch(0.14 0.04 240)", fontSize: "14px",
        }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔒</div>
          <p style={{ fontWeight: 600, color: "oklch(0.14 0.04 240)" }}>Access Restricted</p>
          <p>The Document Folder is available to Admin, Controller, Bookkeeping Manager, Bookkeeper Supervisor, Bookkeepers, and Tax Division staff only.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Shift content left when preview is open */}
      <div style={{
        maxWidth: previewFile ? "calc(100% - 540px)" : "900px",
        margin: "0 auto",
        transition: "max-width 0.2s ease",
      }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
          <div>
            <h1 style={{ fontSize: "20px", fontWeight: 700, color: "oklch(0.18 0.02 240)", margin: 0 }}>
              Documents
            </h1>
            <p style={{ fontSize: "13px", color: "oklch(0.14 0.04 240)", margin: "3px 0 0" }}>
              Shared workspace — Admin, Controller, Bkkpr Mgr, Irit, Bookkeepers, Tax Division
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {!canWrite && (
              <span style={{ fontSize: "12px", color: "oklch(0.45 0.03 240)", fontStyle: "italic" }}>
                Read-only access
              </span>
            )}
            {canWrite && (
              <>
                <button
                  onClick={() => setShowNewFolder(true)}
                  style={{
                    padding: "7px 14px", borderRadius: "6px", fontSize: "13px", fontWeight: 600,
                    background: "oklch(0.94 0.018 220)", color: "oklch(0.08 0.01 240)",
                    border: "1px solid oklch(0.92 0.005 240 / 30%)", cursor: "pointer",
                  }}
                >
                  + New Folder
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{
                    padding: "7px 14px", borderRadius: "6px", fontSize: "13px", fontWeight: 600,
                    background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)",
                    border: "1px solid oklch(0.92 0.005 240 / 35%)", cursor: uploading ? "default" : "pointer",
                    opacity: uploading ? 0.6 : 1,
                  }}
                >
                  {uploading ? "Uploading…" : "Upload File"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_TYPES.join(",")}
                  style={{ display: "none" }}
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </>
            )}
          </div>
        </div>

        {/* ── Breadcrumbs ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: "6px",
          marginBottom: "16px", fontSize: "13px",
        }}>
          {breadcrumbs.map((crumb, idx) => (
            <span key={idx} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {idx > 0 && <span style={{ color: "oklch(0.14 0.04 240)" }}>›</span>}
              <button
                onClick={() => navigateTo(crumb, idx)}
                style={{
                  background: "none", border: "none", cursor: "pointer", padding: 0,
                  color: idx === breadcrumbs.length - 1
                    ? "oklch(0.82 0.01 240)"
                    : "oklch(0.60 0.02 240)",
                  fontWeight: idx === breadcrumbs.length - 1 ? 600 : 400,
                  fontSize: "13px",
                }}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </div>

        {/* ── New folder input ── */}
        {showNewFolder && canWrite && (
          <div style={{
            display: "flex", alignItems: "center", gap: "8px",
            marginBottom: "16px", padding: "12px 16px",
            background: "oklch(0.93 0.008 240)", borderRadius: "8px",
            border: "1px solid oklch(0.88 0.01 240)",
          }}>
            <span style={{ fontSize: "18px" }}>📁</span>
            <input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateFolder();
                if (e.key === "Escape") { setShowNewFolder(false); setNewFolderName(""); }
              }}
              placeholder="Folder name…"
              style={{
                flex: 1, background: "transparent", border: "none", outline: "none",
                color: "oklch(0.18 0.02 240)", fontSize: "14px",
              }}
            />
            <button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || createFolderMutation.isPending}
              style={{
                padding: "5px 12px", borderRadius: "5px", fontSize: "13px", fontWeight: 600,
                background: "oklch(0.92 0.005 240 / 20%)", color: "oklch(0.08 0.01 240)",
                border: "1px solid oklch(0.92 0.005 240 / 35%)", cursor: "pointer",
              }}
            >
              Create
            </button>
            <button
              onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}
              style={{
                padding: "5px 10px", borderRadius: "5px", fontSize: "13px",
                background: "transparent", color: "oklch(0.14 0.04 240)",
                border: "1px solid oklch(0.88 0.01 240)", cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {/* ── Drop zone (empty folder) ── */}
        <div
          onDragOver={(e) => { if (canWrite) { e.preventDefault(); setIsDragging(true); } }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={canWrite ? handleDrop : undefined}
          style={{
            minHeight: "60px",
            border: `2px dashed ${isDragging ? "oklch(0.82 0.01 240)" : "oklch(0.88 0.01 240)"}`,
            borderRadius: "10px",
            background: isDragging ? "oklch(0.92 0.005 240 / 6%)" : "transparent",
            display: folders.length === 0 && files.length === 0 && !isLoading ? "flex" : "none",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 24px",
            marginBottom: "16px",
            transition: "all 0.15s",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <div style={{ fontSize: "32px" }}>📂</div>
          <p style={{ color: "oklch(0.14 0.04 240)", fontSize: "14px", margin: 0, textAlign: "center" }}>
            {canWrite ? (
              <><span style={{ color: "oklch(0.08 0.01 240)" }}>Drag & drop files here</span> or click "Upload File" above.</>
            ) : (
              <>This folder is empty.<br />You have read-only access.</>
            )}
          </p>
        </div>

        {/* ── Loading ── */}
        {isLoading && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "oklch(0.14 0.04 240)", fontSize: "14px" }}>
            Loading…
          </div>
        )}

        {/* ── Folders grid ── */}
        {folders.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <p style={{ fontSize: "13px", fontWeight: 600, color: "oklch(0.14 0.04 240)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>
              Folders
            </p>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: "10px",
            }}>
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  style={{
                    background: "oklch(0.93 0.008 240)",
                    border: "1px solid oklch(0.90 0.009 240)",
                    borderRadius: "8px",
                    padding: "14px 14px 10px",
                    cursor: "pointer",
                    transition: "border-color 0.15s, background 0.15s",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.92 0.005 240 / 50%)";
                    (e.currentTarget as HTMLElement).style.background = "oklch(0.95 0.006 240)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.90 0.009 240)";
                    (e.currentTarget as HTMLElement).style.background = "oklch(0.93 0.008 240)";
                  }}
                  onClick={() => openFolder(folder)}
                >
                  <div style={{ fontSize: "28px", marginBottom: "6px" }}>📁</div>
                  <div style={{
                    fontSize: "13px", fontWeight: 600, color: "oklch(0.20 0.02 240)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {folder.name}
                  </div>
                  <div style={{ fontSize: "13px", color: "oklch(0.14 0.04 240)", marginTop: "2px" }}>
                    {formatDate(folder.createdAt)}
                  </div>
                  {/* Delete button — hidden for read-only users */}
                  {canWrite && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Delete folder "${folder.name}" and all its contents?`)) {
                          setDeletingId(folder.id);
                          deleteFolderMutation.mutate(
                            { id: folder.id, callerEmail: userEmail },
                            { onSettled: () => setDeletingId(null) }
                          );
                        }
                      }}
                      style={{
                        position: "absolute", top: "8px", right: "8px",
                        background: "none", border: "none", cursor: "pointer",
                        color: "oklch(0.28 0.03 240)", fontSize: "14px", padding: "2px",
                        opacity: deletingId === folder.id ? 0.4 : 0,
                        transition: "opacity 0.15s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.opacity = "1"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.opacity = deletingId === folder.id ? "0.4" : "0"}
                      title="Delete folder"
                    >
                      🗑
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Files list ── */}
        {files.length > 0 && (
          <div>
            <p style={{ fontSize: "13px", fontWeight: 600, color: "oklch(0.14 0.04 240)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>
              Files
            </p>

            <div
              onDragOver={(e) => { if (canWrite) { e.preventDefault(); setIsDragging(true); } }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={canWrite ? handleDrop : undefined}
              style={{
                border: `2px dashed ${isDragging ? "oklch(0.82 0.01 240)" : "transparent"}`,
                borderRadius: "8px",
                transition: "border-color 0.15s",
                padding: isDragging ? "4px" : "0",
              }}
            >
              <div style={{
                background: "oklch(0.95 0.006 240)",
                border: "1px solid oklch(0.92 0.008 240)",
                borderRadius: "8px",
                overflow: "hidden",
              }}>
                {/* Table header */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 80px 120px 120px",
                  padding: "8px 16px",
                  background: "oklch(0.93 0.008 240)",
                  borderBottom: "1px solid oklch(0.88 0.01 240)",
                  fontSize: "13px", fontWeight: 600, color: "oklch(0.28 0.04 240)",
                  letterSpacing: "0.06em", textTransform: "uppercase",
                }}>
                  <span>Name</span>
                  <span>Size</span>
                  <span>Uploaded by</span>
                  <span style={{ textAlign: "right" }}>Actions</span>
                </div>

                {files.map((file, idx) => {
                  const isSelected = previewFile?.id === file.id;
                  const canPreview = isPreviewable(file.mimeType);
                  return (
                    <div
                      key={file.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 80px 120px 120px",
                        padding: "11px 16px",
                        borderBottom: idx < files.length - 1 ? "1px solid oklch(0.95 0.006 240)" : "none",
                        alignItems: "center",
                        background: isSelected ? "oklch(0.92 0.005 240 / 8%)" : "transparent",
                        transition: "background 0.1s",
                        cursor: canPreview ? "pointer" : "default",
                        borderLeft: isSelected ? "2px solid oklch(0.82 0.01 240)" : "2px solid transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) (e.currentTarget as HTMLElement).style.background = "oklch(0.93 0.008 240)";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) (e.currentTarget as HTMLElement).style.background = "transparent";
                      }}
                      onClick={() => canPreview && handleFileClick(file as PreviewFile)}
                    >
                      {/* Name + icon */}
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                        <span style={{ fontSize: "18px", flexShrink: 0 }}>{getFileIcon(file.mimeType)}</span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontSize: "13px", fontWeight: 500,
                            color: canPreview ? "oklch(0.08 0.01 240)" : "oklch(0.14 0.04 240)",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {file.name}
                            {canPreview && (
                              <span style={{
                                marginLeft: "6px", fontSize: "10px",
                                color: "oklch(0.08 0.01 240)", fontWeight: 400,
                              }}>
                                click to preview
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: "13px", color: "oklch(0.28 0.04 240)" }}>
                            {formatDate(file.createdAt)}
                          </div>
                        </div>
                      </div>

                      {/* Size */}
                      <span style={{ fontSize: "13px", color: "oklch(0.14 0.04 240)" }}>
                        {formatSize(file.size)}
                      </span>

                      {/* Uploaded by */}
                      <span style={{
                        fontSize: "13px", color: "oklch(0.14 0.04 240)",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {file.uploadedByName}
                      </span>

                      {/* Actions */}
                      <div
                        style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => handleDownload(file.s3Url, file.name)}
                          title="Download"
                          style={{
                            padding: "5px 10px", borderRadius: "5px", fontSize: "13px",
                            background: "oklch(0.92 0.005 240 / 12%)", color: "oklch(0.08 0.01 240)",
                            border: "1px solid oklch(0.92 0.005 240 / 30%)", cursor: "pointer",
                          }}
                        >
                          ↓
                        </button>
                        {canWrite && (
                          <button
                            onClick={() => {
                              if (confirm(`Delete "${file.name}"?`)) {
                                deleteFileMutation.mutate({ id: file.id, callerEmail: userEmail });
                              }
                            }}
                            title="Delete"
                            style={{
                              padding: "5px 10px", borderRadius: "5px", fontSize: "13px",
                              background: "oklch(0.94 0.018 220)", color: "oklch(0.14 0.04 240)",
                              border: "1px solid oklch(0.88 0.01 240)", cursor: "pointer",
                            }}
                          >
                            🗑
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Drag overlay when folder has content ── */}
        {(folders.length > 0 || files.length > 0) && canWrite && (
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            style={{
              marginTop: "16px",
              padding: "16px",
              border: `2px dashed ${isDragging ? "oklch(0.82 0.01 240)" : "oklch(0.90 0.009 240)"}`,
              borderRadius: "8px",
              textAlign: "center",
              fontSize: "13px",
              color: isDragging ? "oklch(0.08 0.01 240)" : "oklch(0.35 0.02 240)",
              transition: "all 0.15s",
              background: isDragging ? "oklch(0.92 0.005 240 / 5%)" : "transparent",
            }}
          >
            {isDragging ? "Drop files here to upload" : "Drag & drop files here to upload"}
          </div>
        )}

      </div>

      {/* ── Preview panel (fixed right side) ── */}
      {previewFile && (
        <PreviewPanel
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onDownload={handleDownload}
        />
      )}
    </DashboardLayout>
  );
}
